import { useEffect, useState, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { StatusCodes } from 'http-status-codes';
import {
  PalletsPerStatusDto,
  ProductWithQuantitiesGetDto,
  QuantityGetDtoToStockTransactionAddDto,
  SellStockTransactionDto,
  StockTransactionAddDto,
} from 'svila-common';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';
import InventoryApi from '~/src/services/api/InventoryApi';
import WhitePad from '../../../../../components/common/WhitePad/WhitePad';
import SecondaryNavigation from '../../../../../components/common/navigation/SecondaryNavigation';
import TitleComponent from '../../../../../components/common/forms/Title/TitleComponent';
import StatusComponent from './components/StatusComponent';
import OrderProductsApi from '~/src/services/api/OrderProductsApi';
import { Formik } from 'formik';
import AutocompleteComponent from '~/src/components/common/forms/Autcomplete/AutocompleteComponent';
import PrimaryButton from '~/src/components/common/forms/Button/PrimaryButton';
import { API_URL, CONSOLIDATION_STATUS } from '~/src/utils/constants';
import ResultDtoDisplay from '~/src/scenes/imports/components/ResultDtoDisplay';
import SecondaryButton from '~/src/components/common/forms/Button/SecondaryButton';
import ExternalUsersApi from '~/src/services/api/ExternalUsersApi';
import OrdersApi from '~/src/services/api/OrdersApi';
import MyContext from '~/src/services/context';
import { quantityGetDtoToStockTransactionAddDto } from '~/src/types/convertors/quantityGetDtoToStockTransactionAddDto';

interface ParamProps {
  id: string;
}

export default function StatusDashboard(): JSX.Element {
  const { id, consolidation } = useParams<ParamProps>();
  const context = useContext(MyContext);
  const history = useHistory();
  const [products, setProducts] = useState<ProductWithQuantitiesGetDto[] | null>(null);
  const [palletsPerStatus, setPalletsPerStatus] = useState<PalletsPerStatusDto | null>(null);
  const [consolidationStatus, setConsolidationStatus] = useState<string | null>(null);
  const [result, setResult] = useState(null);
  const [filteredQuantities, setFilteredQuantities] = useState<StockTransactionAddDto>([]);
  const [listOfChecked, setListOfChecked] = useState([]);

  const getProducts = async (): Promise<void> => {
    const result = await InventoryApi.getReserved(id);
    if (result.status === StatusCodes.OK) {
      setProducts(result.data);
    }
  };

  const getPalletCounts = async (): Promise<void> => {
    const result = await OrderProductsApi.getPalletCountForStatuses(id);
    if (result?.status === StatusCodes.OK) {
      setPalletsPerStatus(result.data);
    }
  };

  const getOrder = async (): Promise<void> => {
    const result = await OrdersApi.get(id);
    if (result?.status === StatusCodes.OK) {
      setConsolidationStatus(result.data.consolidationStatus);
    }
  };

  useEffect(() => {
    getProducts();
    getPalletCounts();
    getOrder();
  }, []);

  const handleClick = (event) => {
    const tempId = event.target.name;
    const checked = event.target.checked;

    setListOfChecked([...listOfChecked, tempId]);
    if (!checked) {
      setListOfChecked(listOfChecked.filter((item) => item !== tempId));
    }
  };

  useEffect(() => {
    const filteredQuantitiesForProducts: StockTransactionAddDto = products?.reduce((accumulator, product) => {
      const filteredQuantitiesForProduct = product.quantities.filter((quantity) =>
        listOfChecked.includes(quantity.tempId),
      );
      const filteredQuantitiesForProductDto = filteredQuantitiesForProduct.map((filteredQuantity) =>
        quantityGetDtoToStockTransactionAddDto(filteredQuantity),
      );
      return accumulator.concat(filteredQuantitiesForProductDto);
    }, []);

    setFilteredQuantities(filteredQuantitiesForProducts);
  }, [listOfChecked]);

  const sellInventories = async (values, setSubmitting): Promise<void> => {
    setSubmitting(true);
    const data: SellStockTransactionDto = {
      stockTransactions: filteredQuantities,
      newOwnerId: values.warehouseOwnerId,
    };
    const result = await InventoryApi.sell(data);

    if (result.status === StatusCodes.OK) {
      setResult(result.data);
      updatedProducts();
    }

    setSubmitting(false);
  };

  const updatedProducts = () => {
    if (products) {
      const updatedProductsList = products.map((product) => {
        const updatedQuantities = product.quantities.map((quantity) => {
          if (listOfChecked.includes(quantity.tempId)) {
            return { ...quantity, isSold: true };
          }
          return quantity;
        });
        return { ...product, quantities: updatedQuantities };
      });

      return setProducts(updatedProductsList);
    }
  };

  const changeStatus = async (data, id: string) => {
    const status = { status: data };
    const result = await ExternalUsersApi.changeStatus(status, id);
    if (result.status === StatusCodes.OK) {
      setConsolidationStatus(result.data.consolidationStatus);
    }
  };

  return (
    <WhitePad isFluid>
      <DndProvider backend={HTML5Backend}>
        <SecondaryNavigation isConsolidation={consolidation} />
        <TitleComponent
          title="Status Dashboard"
          onLeftHandler={(): void => {
            history.push('/orders-list');
          }}
        />
        {context.user.role === 'WarehouseExternalUser' && (
          <>
            {(consolidationStatus == null || consolidationStatus == CONSOLIDATION_STATUS.CanceledByOwner) && (
              <SecondaryButton
                title="Assign for Consolidation"
                onClick={(): void => changeStatus(CONSOLIDATION_STATUS.ConfirmedByOwner, id)}
              />
            )}
            {consolidationStatus == CONSOLIDATION_STATUS.ConfirmedByOwner && (
              <SecondaryButton
                title="Cancel Consolidation"
                onClick={(): void => changeStatus(CONSOLIDATION_STATUS.CanceledByOwner, id)}
              />
            )}
            {consolidationStatus == CONSOLIDATION_STATUS.InConsolidation && <p>In Consolidation</p>}
            {consolidationStatus == CONSOLIDATION_STATUS.Consolidated && <p>Consolidated</p>}
          </>
        )}
        {listOfChecked.length !== 0 && (
          <div className="container" style={{ display: 'flex', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Formik
                style={{ display: 'flex', alignItems: 'flex-end' }}
                initialValues={{
                  warehouseOwnerId: products?.[0].quantities[0].newWarehouseOwnerId,
                }}
                onSubmit={(values, { setSubmitting }) => {
                  sellInventories(values, setSubmitting);
                }}
              >
                {({
                  initialValues,
                  values,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue,
                  errors,
                  touched,
                }) => (
                  <form
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      height: 'fit-content',
                      marginRight: '150px',
                    }}
                    onSubmit={handleSubmit}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid #ccc',
                        padding: '5px',
                        marginRight: '10px',
                      }}
                    >
                      <p style={{ marginBottom: 0, fontWeight: 'bold' }}>
                        {products?.[0]?.quantities[0]?.sellOrder?.customer?.name}
                      </p>
                    </div>

                    <PrimaryButton isSubmitting={isSubmitting} type="submit" title="SELL" />
                  </form>
                )}
              </Formik>
            </div>
            <ResultDtoDisplay result={result} />
          </div>
        )}
        <ContainerWrapper>
          <Wrapper>
            <PalletPerStatus color={'#8F0CF5'}>{palletsPerStatus?.waitingPallets.toFixed(2)}</PalletPerStatus>
          </Wrapper>
          <Wrapper>
            <PalletPerStatus color={'#EFBB03'}>{palletsPerStatus?.labellingPallets.toFixed(2)}</PalletPerStatus>
          </Wrapper>
          <Wrapper>
            <PalletPerStatus color={'#0F6DF9'}>
              {palletsPerStatus?.consolidationPallets.toFixed(2)}
            </PalletPerStatus>
          </Wrapper>
          <Wrapper>
            <PalletPerStatus color={'#05B40C'}>{palletsPerStatus?.readyPallets.toFixed(2)}</PalletPerStatus>
          </Wrapper>
        </ContainerWrapper>
        {products && (
          <Container>
            {products?.map((item: ProductWithQuantitiesGetDto, index: number) => (
              <StatusComponent
                palletsPerStatus={palletsPerStatus}
                productData={item}
                key={item.product.id}
                index={index + 1}
                handleClick={handleClick}
                isCheck={listOfChecked}
                getProducts={getProducts}
              />
            ))}
          </Container>
        )}
      </DndProvider>
    </WhitePad>
  );
}

const ContainerWrapper = styled(Container)`
  display: flex;
  justify-content: space-betweeen;
  width: 100%;
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 0;
`;

const PalletPerStatus = styled.div`
  position: relative;
  padding-top: 20px;
  text-align: center;
  color: #343434;
  font-size: 15px;
  line-height: 16px;
  font-weight: 700;
  margin: 10px;
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 5px;
    left: 0;
    right: 5px;
    height: 9px;
    background-color: ${(props) => {
    return props.color;
  }};
    border-radius: 10px;
  }
`;

const Wrapper = styled.div`
  width: 25%;
  background: #f4f4f4;
  border-radius: 20px;
  margin: 5px;
`;
